import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import type { FeatureViewModel } from '@/features/patient-record/types';

import { SectionHeading } from './SummaryItem';

const PRESENT_LIMIT = 8;
const ABSENT_LIMIT = 5;
const UNKNOWN_LIMIT = 5;

interface FeaturesTooltipProps {
  readonly present: readonly FeatureViewModel[];
  readonly absent: readonly FeatureViewModel[];
  readonly unknown?: readonly FeatureViewModel[];
}

export function FeaturesTooltip({
  present,
  absent,
  unknown = [],
}: FeaturesTooltipProps): ReactElement {
  const { t } = useTranslation();

  return (
    <div className="space-y-2 min-w-[240px]">
      {present.length > 0 && (
        <FeatureGroup
          heading={t('patient.geneticSummary.tooltips.presentTitle', {
            count: present.length,
          })}
          dotClassName="bg-primary"
          features={present}
          textClassName="text-foreground"
          limit={PRESENT_LIMIT}
        />
      )}
      {absent.length > 0 && (
        <>
          {present.length > 0 && <hr className="border-border" />}
          <FeatureGroup
            heading={t('patient.geneticSummary.tooltips.absentTitle', {
              count: absent.length,
            })}
            dotClassName="bg-muted-foreground"
            features={absent}
            textClassName="text-foreground/70"
            limit={ABSENT_LIMIT}
          />
        </>
      )}
      {unknown.length > 0 && (
        <>
          {(present.length > 0 || absent.length > 0) && (
            <hr className="border-border" />
          )}
          <FeatureGroup
            heading={t('patient.geneticSummary.tooltips.unknownTitle', {
              count: unknown.length,
            })}
            dotClassName="bg-muted"
            features={unknown}
            textClassName="text-foreground/60"
            limit={UNKNOWN_LIMIT}
          />
        </>
      )}
    </div>
  );
}

interface FeatureGroupProps {
  readonly heading: string;
  readonly dotClassName: string;
  readonly features: readonly FeatureViewModel[];
  readonly textClassName: string;
  readonly limit: number;
}

function FeatureGroup({
  heading,
  dotClassName,
  features,
  textClassName,
  limit,
}: FeatureGroupProps): ReactElement {
  const { t } = useTranslation();
  const visible = features.slice(0, limit);
  const overflow = features.length - visible.length;

  return (
    <div className="space-y-1">
      <SectionHeading text={heading} />
      <ul className="space-y-0.5">
        {visible.map((feature) => (
          <li key={feature.id} className="flex items-center gap-1.5 text-xs">
            <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', dotClassName)} />
            <span className={cn('truncate', textClassName)}>{feature.label}</span>
            <span className="text-muted-foreground/80 ml-auto text-[10px] shrink-0 tabular-nums">
              {feature.hpoId}
            </span>
          </li>
        ))}
      </ul>
      {overflow > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t('patient.geneticSummary.tooltips.andMore', { count: overflow })}
        </p>
      )}
    </div>
  );
}
